// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
  },
}));

const LandPrepPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedPreIrrigationDate, setSelectedPreIrrigationDate] =
    useState(null);
  const [selectedLandPreparationDate, setSelectedLandPreparationDate] =
    useState(null);
  const [comment, setComment] = useState("");
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { showAlert } = useAlert();
  const [frequencyData, setFrequencyData] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [preIrrigationError, setPreIrrigationError] = useState(false);
  const [landPreparationError, setLandPreparationError] = useState(false);

  const [dropdownValues, setDropdownValues] = useState({
    cultivator: "Select",
    plough: "Select",
    chiselPlough: "Select",
    others: "Select",
  });
  let farmerPlotId = getFarmerPlotId();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-land-preparation?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    const fetchFrequencyData = async () => {
      try {
        const response = await instance.get("plotengagement/get-frequency");
        setFrequencyData(response.data);
      } catch (error) {
        console.error("Error fetching frequency data:", error);
      }
    };

    fetchFrequencyData();
  }, []);
  useEffect(() => {
    if (existingData) {
      setSelectedPreIrrigationDate(new Date(existingData.pre_irrgation_date));
      setSelectedLandPreparationDate(
        new Date(existingData.land_preparation_date)
      );
      setCheckboxValues({
        cultivator: !!existingData.implants.cultivator,
        plough: !!existingData.implants.plough,
        chiselPlough: !!existingData.implants.chiselPlough,
        others: !!existingData.implants.others,
      });
      setDropdownValues({
        cultivator: existingData.implants.cultivator || "Select",
        plough: existingData.implants.plough || "Select",
        chiselPlough: existingData.implants.chiselPlough || "Select",
        others: existingData.implants.others || "Select",
      });
      setComment(existingData.comments || "");
      setIsEditMode(false);
    }
  }, [existingData]);
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handlePreIrrigationDateChange = (date) => {
    setSelectedPreIrrigationDate(date);
  };
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleLandPreparationDateChange = (date) => {
    setSelectedLandPreparationDate(date);
  };
  const checkboxData = [
    { name: t("Cultivator"), key: "cultivator" },
    { name: t("Plough"), key: "plough" },
    { name: t("Chisel Plough"), key: "chiselPlough" },
    { name: t("Others"), key: "others" },
  ];

  const [checkboxValues, setCheckboxValues] = useState({
    cultivator: false,
    plough: false,
    chiselPlough: false,
    others: false,
  });

  const handleDropdownChange = (dropdownName, value) => {
    setDropdownValues((prevValues) => ({
      ...prevValues,
      [dropdownName]: value,
    }));
  };
  const handleCheckboxChange = (checkboxName) => {
    setCheckboxValues((prevValues) => ({
      ...prevValues,
      [checkboxName]: !prevValues[checkboxName],
    }));
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedPreIrrigationDate || !selectedLandPreparationDate) {
      setPreIrrigationError(!selectedPreIrrigationDate);
      setLandPreparationError(!selectedLandPreparationDate);
      return;
    }
    try {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const preIrrigationDate = formatDate(selectedPreIrrigationDate);
      const landPreparationDate = formatDate(selectedLandPreparationDate);
      if (isEditMode) {
        const implants = {};
        checkboxData.forEach(({ key }) => {
          if (checkboxValues[key]) {
            implants[key] = Number(dropdownValues[key]);
          }
        });
        const updateData = {
          id: existingData.id,
          pre_irrgation_date: preIrrigationDate,
          land_preparation_date: landPreparationDate,
          implants: implants,
          comments: comment,
        };

        await instance.post(
          "plotengagement/update-land-preparation",
          updateData
        );
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
          onClose();
        }, 2000);
      } else {
        const implants = {};
        checkboxData.forEach(({ key }) => {
          if (checkboxValues[key]) {
            implants[key] = Number(dropdownValues[key]);
          }
        });
        const data = {
          farmer_plot: farmerPlotId,
          pre_irrgation_date: preIrrigationDate,
          land_preparation_date: landPreparationDate,
          implants: implants,
          comments: comment,
        };
        await instance.post("plotengagement/create-land-preparation", data);
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error("error creating land-preparation", error);
      showAlert("error", "Some Error Occured");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "10px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle>{t("Land Preparation")}</DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img src={EditIcon} onClick={handleEditClick} alt="Edit" />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={7} style={{ marginTop: "25px" }}>
              <InputLabel style={{ marginLeft: "1rem", fontSize: "12px" }}>
                {t("Date of pre-irrigation")}
              </InputLabel>
            </Grid>
            <Grid item xs={5}>
              <TextField
                placeholder="DD/MM/YYYY"
                fullWidth
                disabled={!isEditMode && existingData !== null}
                value={
                  selectedPreIrrigationDate
                    ? selectedPreIrrigationDate.toLocaleDateString()
                    : ""
                }
                error={preIrrigationError}
                helperText={preIrrigationError ? t("Please select date") : ""}
                InputProps={{
                  sx: {
                    height: "40px",
                    borderRadius: "5px",
                    marginTop: "20px",
                    background: "#FFF",
                    border: "1px solid #C7BDBD",
                  },
                  readOnly: true,
                  onClick: () => {
                    if (isEditMode || existingData === null) {
                      document
                        .getElementById("pre-irrigation-date-picker")
                        .click();
                    }
                  },
                }}
              />
            </Grid>
            <DatePicker
              id="pre-irrigation-date-picker"
              dateFormat="dd/MM/yyyy"
              selected={selectedPreIrrigationDate}
              onChange={handlePreIrrigationDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <Grid container>
            <Grid item xs={7} style={{ marginTop: "25px" }}>
              <InputLabel style={{ marginLeft: "1rem", fontSize: "12px" }}>
                {t("Date of land preparation")}
              </InputLabel>
            </Grid>
            <Grid item xs={5}>
              <TextField
                placeholder="DD/MM/YYYY"
                fullWidth
                value={
                  selectedLandPreparationDate
                    ? selectedLandPreparationDate.toLocaleDateString()
                    : ""
                }
                error={landPreparationError}
                helperText={landPreparationError ? "Please select date" : ""}
                disabled={!isEditMode && existingData !== null}
                InputProps={{
                  sx: {
                    height: "40px",
                    borderRadius: "5px", // You can adjust this if needed
                    marginTop: "20px",
                    background: "#FFF",
                    border: "1px solid #C7BDBD",
                  },
                  readOnly: true,
                  onClick: () => {
                    if (isEditMode || existingData === null) {
                      document
                        .getElementById("land-preparation-date-picker")
                        .click();
                    }
                  },
                }}
              />
            </Grid>
            <DatePicker
              id="land-preparation-date-picker"
              dateFormat="dd/MM/yyyy"
              selected={selectedLandPreparationDate}
              onChange={handleLandPreparationDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <Grid container style={{ marginTop: "10px" }}>
            <Grid item xs={8}>
              <Typography>{t("Types of Implements")}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{t("Frequency")}</Typography>
            </Grid>
          </Grid>
          <Grid container>
            {checkboxData.map(({ name, key }) => (
              <>
                {/* Checkbox */}
                <Grid item xs={2} style={{ marginTop: "20px" }}>
                  <Checkbox
                    checked={checkboxValues[key]}
                    onChange={() => handleCheckboxChange(key)}
                    disabled={!isEditMode && existingData !== null}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputLabel style={{ fontSize: "12px", marginTop: "32px" }}>
                    {name}
                  </InputLabel>
                </Grid>
                <Grid item xs={4}>
                  <Select
                    value={dropdownValues[key]}
                    onChange={(e) => handleDropdownChange(key, e.target.value)}
                    style={{ width: "100px", marginTop: "18px" }}
                    sx={{
                      height: "40px", // Set the desired height
                      border: "1px solid #9C9FA0",
                      borderRadius: "5px",
                    }}
                    disabled={
                      (!isEditMode && existingData !== null) ||
                      !checkboxValues[key]
                    }
                  >
                    {/* <MenuItem value='Select' disabled>
											Select
										</MenuItem> */}
                    {/* Rendering other frequency options */}
                    {frequencyData.map((frequency) => (
                      <MenuItem key={frequency.id} value={frequency.name}>
                        {frequency.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </>
            ))}
          </Grid>
          <div style={{ display: "grid", padding: "10px" }}>
            <InputLabel>{t("Comment (Optional)")}</InputLabel>
            <TextField
              multiline
              rows={1}
              value={comment}
              onChange={handleCommentChange}
              disabled={!isEditMode && existingData !== null}
            />
          </div>
          <DialogActions>
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
              }}
            >
              {existingData && isEditMode ? (
                <div style={{ width: "140px" }}>
                  <CustomButton onClick={handleSubmit} hideIcon>
                    {t("Update")}
                  </CustomButton>
                </div>
              ) : !existingData && !isEditMode ? (
                <div
                  style={{
                    display: "flex",
                    margin: "auto",
                    justifyContent: "center",
                    width: "140px",
                  }}
                >
                  <CustomButton onClick={handleSubmit} hideIcon>
                    {t("Submit")}
                  </CustomButton>
                </div>
              ) : null}
            </div>
          </DialogActions>
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default LandPrepPopup;
